import React, { useState, useEffect } from 'react';
import AdminLayoutWrapper from '../../components/layout/AdminLayoutWrapper';
import DataTableLayout from '../../components/common/DataTableLayout';
import FilterPanel from '../../components/common/FilterPanel';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Chip,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import {
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
} from '@mui/x-data-grid';
import { BOARDS_EXAMS, STATUSES } from '../../config/Config';
import { useNavigate } from 'react-router-dom';
import { createFilterChangeHandler, clearFilters, filterData, dataGridProps } from '../../utils/tableUtils';
import api from '../../services/api';

export default function BoardManagementPage() {
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ name: '' });
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', status: '' });
  const [statusOptions, setStatusOptions] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this board?')) return;
    try {
      await api.delete(`${BOARDS_EXAMS}${id}/`);
      setBoards(prev => prev.filter(b => b.id !== id));
      showSnackbar('Board deleted successfully');
    } catch (err) {
      console.error('Error deleting board:', err);
      showSnackbar('Failed to delete board', 'error');
    }
  };

  const fetchPaginated = async (endpoint) => {
    let url = endpoint;
    let all = [];
    while (url) {
      const res = await api.get(url);
      const data = res.data;
      if (Array.isArray(data)) {
        all = data;
        break;
      }
      all = all.concat(data.results || []);
      url = data.next || null;
    }
    return all;
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const allBoards = await fetchPaginated(BOARDS_EXAMS);
      setBoards(allBoards);
    } catch (err) {
      console.error('Error fetching boards:', err);
      showSnackbar('Failed to load boards', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        const res = await api.get(STATUSES);
        const list = Array.isArray(res.data) ? res.data : (res.data?.results || []);
        const draftPublished = list.filter(s => 
          (s.status_id || s.statusId || '').toUpperCase() === 'DRAFT' || 
          (s.status_id || s.statusId || '').toUpperCase() === 'PUBLISHED'
        );
        setStatusOptions(draftPublished);
      } catch (err) {
        setStatusOptions([]);
      }
    };
    fetchStatuses();
  }, []);

  const handleOpen = (item = null) => {
    if (item) {
      setForm({ name: item.name, status: item.status || '' });
      setEditMode(true);
      setCurrentId(item.id);
    } else {
      setForm({ name: '', status: '' });
      setEditMode(false);
      setCurrentId(null);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setForm({ name: '', status: '' });
    setEditMode(false);
    setCurrentId(null);
  };

  const handleSubmit = async () => {
    if (!form.name) {
      showSnackbar('Please enter a board name', 'error');
      return;
    }

    try {
      const payload = { name: form.name };
      if (form.status) payload.status = Number(form.status);
      if (editMode) {
        await api.put(`${BOARDS_EXAMS}${currentId}/`, payload);
        showSnackbar('Board updated successfully');
      } else {
        const res = await api.post(BOARDS_EXAMS, payload);
        setBoards(prev => [...prev, res.data]);
        showSnackbar('Board created successfully');
      }
      handleClose();
      fetchAll();
    } catch (err) {
      console.error('Error saving board:', err);
      showSnackbar('Failed to save board', 'error');
    }
  };

  const filteredBoards = filterData(boards, filters);

  const handleFilterChange = createFilterChangeHandler(setFilters);
  const handleClearFilters = () => clearFilters(setFilters, { name: '' });

  const filterConfig = [
    {
      field: 'name',
      label: 'Name',
      type: 'text',
      placeholder: 'Search by board name...'
    }
  ];

  const columns = [
    {
      field: 'name',
      headerName: 'Board Name',
      flex: 2,
      renderCell: (params) => (
        <Chip 
          label={params.value} 
          size="small" 
          color="primary" 
          variant="outlined"
        />
      )
    },
    {
      field: 'status_value',
      headerName: 'Status',
      flex: 1,
      renderCell: (params) => params.value ? (
        <Chip label={params.value} size="small" variant="outlined" />
      ) : '-'
    },
    {
      field: 'created_at',
      headerName: 'Created',
      flex: 1,
      valueGetter: (params) => params.value ? new Date(params.value).toLocaleDateString() : 'N/A'
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => handleOpen(params.row)}
              sx={{ p: 0.5 }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={() => handleDelete(params.row.id)}
              sx={{ p: 0.5, color: 'error.main' }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  const actionButtons = [
    {
      label: 'Add Board',
      icon: <AddIcon />,
      variant: 'contained',
      onClick: () => handleOpen(null)
    }
  ];

  const emptyStateProps = {
    title: 'No boards found',
    description: 'Try adjusting your filters or add your first board.',
    icon: <AddIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
  };

  return (
    <AdminLayoutWrapper>
      <DataTableLayout
        title="Boards"
        actionButtons={actionButtons}
        filterPanel={
          <FilterPanel
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            filterConfig={filterConfig}
          />
        }
        loading={loading}
        isEmpty={!loading && filteredBoards.length === 0}
        emptyStateProps={emptyStateProps}
        mobileFilterOpen={mobileFilterOpen}
        onMobileFilterClose={() => setMobileFilterOpen(!mobileFilterOpen)}
      >
        <DataGrid
          rows={filteredBoards}
          columns={columns}
          {...dataGridProps}
          components={{
            Toolbar: () => (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mb: 2,
                p: 1
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <GridToolbarColumnsButton 
                    sx={{ 
                      color: 'primary.main',
                      '&:hover': { backgroundColor: 'primary.50' }
                    }}
                  />
                  <GridToolbarDensitySelector 
                    sx={{ 
                      color: 'primary.main',
                      '&:hover': { backgroundColor: 'primary.50' }
                    }}
                  />
                  <GridToolbarExport 
                    sx={{ 
                      color: 'primary.main',
                      '&:hover': { backgroundColor: 'primary.50' }
                    }}
                  />
                </Box>
              </Box>
            )
          }}
        />
      </DataTableLayout>

      {/* Add/Edit Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editMode ? 'Edit Board' : 'Add Board'}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Board Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            fullWidth
            margin="normal"
            required
            autoFocus
            placeholder="e.g., CBSE, ICSE, State Board"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Status</InputLabel>
            <Select
              value={form.status}
              label="Status"
              onChange={(e) => setForm({ ...form, status: e.target.value })}
            >
              <MenuItem value="">None</MenuItem>
              {statusOptions.map((s) => (
                <MenuItem key={s.id} value={String(s.id)}>
                  {s.value || s.status_id || s.statusId}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </AdminLayoutWrapper>
  );
}
